import { useState } from 'react'
import { toast } from 'sonner'
import { Plus, CheckCircle2, XCircle, Pencil } from 'lucide-react'
import {
  useFinancialObligations,
  useCancelObligation,
} from '@/hooks/useFinancialObligations'
import { DataTable } from '@/components/ui/data-table'
import { SearchInput } from '@/components/ui/search-input'
import { Pagination } from '@/components/ui/pagination'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { ObligationStatusBadge } from '@/components/ui/obligation-status-badge'
import { formatBRL } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { ObligationFormModal } from './ObligationFormModal'
import { SettleObligationModal } from './SettleObligationModal'
import type {
  FinancialObligation,
  FinancialObligationType,
} from '@/types/financial-obligation'
import type { Column } from '@/components/ui/data-table'

type StatusFilter = 'all' | 'PENDING' | 'OVERDUE' | 'PAID' | 'CANCELED'

const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'Todas',
  PENDING: 'Em aberto',
  OVERDUE: 'Vencidas',
  PAID: 'Quitadas',
  CANCELED: 'Canceladas',
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}


export function FinancialObligationListPage() {
  const [type, setType] = useState<FinancialObligationType>('PAYABLE')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<FinancialObligation | null>(null)
  const [settleTarget, setSettleTarget] = useState<FinancialObligation | null>(null)
  const [cancelTarget, setCancelTarget] = useState<FinancialObligation | null>(null)
  const cancelMutation = useCancelObligation()

  const params = {
    type,
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(search && { search }),
  }

  const { data, isLoading } = useFinancialObligations({ ...params, page })

  async function handleCancel() {
    if (!cancelTarget) return
    try {
      await cancelMutation.mutateAsync(cancelTarget.id)
      toast.success('Obrigação cancelada')
      setCancelTarget(null)
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message
      toast.error(msg ?? 'Erro ao cancelar')
    }
  }

  function openNew() {
    setEditTarget(null)
    setFormOpen(true)
  }

  const COLUMNS: Column<FinancialObligation>[] = [
    { key: 'dueDate', header: 'Vencimento', render: (row) => formatDate(row.dueDate) },
    { key: 'code', header: 'Código', render: (row) => `O-${row.code}` },
    { key: 'description', header: 'Descrição' },
    {
      key: 'counterparty',
      header: type === 'PAYABLE' ? 'Fornecedor' : 'Cliente',
      className: 'hidden md:table-cell',
      render: (row) => row.counterparty ?? '—',
    },
    {
      key: 'category',
      header: 'Categoria',
      className: 'hidden lg:table-cell',
      render: (row) => row.category.name,
    },
    {
      key: 'amount',
      header: 'Valor',
      render: (row) => (
        <span
          className={cn(
            'font-medium tabular-nums',
            row.type === 'PAYABLE' ? 'text-red-700' : 'text-green-700',
          )}
        >
          {row.type === 'PAYABLE' ? '−' : '+'} {formatBRL(row.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <ObligationStatusBadge status={row.status} isOverdue={row.isOverdue} />
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.status === 'PENDING' ? (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setSettleTarget(row)} title="Quitar">
              <CheckCircle2 size={14} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditTarget(row); setFormOpen(true) }} title="Editar">
              <Pencil size={14} />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCancelTarget(row)} title="Cancelar">
              <XCircle size={14} />
            </Button>
          </div>
        ) : null,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Contas a pagar e receber</h1>
          <p className="text-sm text-muted-foreground">
            Compromissos futuros. Ao quitar, o valor é lançado no fluxo de caixa.
          </p>
        </div>
        <Button onClick={openNew}>
          <Plus size={16} />
          {type === 'PAYABLE' ? 'Nova conta a pagar' : 'Nova conta a receber'}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(['PAYABLE', 'RECEIVABLE'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setType(t)
                setPage(1)
              }}
              className={cn(
                'px-4 py-1.5 transition-colors',
                type === t
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50',
              )}
            >
              {t === 'PAYABLE' ? 'A pagar' : 'A receber'}
            </button>
          ))}
        </div>

        <div className="flex overflow-hidden rounded-md border border-input text-sm">
          {(Object.keys(STATUS_LABEL) as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatusFilter(s)
                setPage(1)
              }}
              className={cn(
                'px-3 py-1.5 transition-colors',
                statusFilter === s
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:bg-muted/50',
              )}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Buscar…"
          className="flex-1 min-w-[180px]"
        />
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <p className="text-xs font-medium text-muted-foreground">
          {type === 'PAYABLE' ? 'Total em aberto a pagar' : 'Total em aberto a receber'}
        </p>
        <p
          className={cn(
            'mt-2 text-xl font-semibold tabular-nums',
            type === 'PAYABLE' ? 'text-red-700' : 'text-green-700',
          )}
        >
          {type === 'PAYABLE' ? '−' : '+'} {formatBRL(data?.openAmount ?? 0)}
        </p>
      </div>

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        emptyTitle={
          type === 'PAYABLE'
            ? 'Nenhuma conta a pagar no filtro'
            : 'Nenhuma conta a receber no filtro'
        }
        emptyDescription="Registre seus compromissos futuros para acompanhar vencimentos."
        emptyAction={
          <Button onClick={openNew}>
            <Plus size={16} />
            {type === 'PAYABLE' ? 'Nova conta a pagar' : 'Nova conta a receber'}
          </Button>
        }
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}

      <ObligationFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false)
          setEditTarget(null)
        }}
        obligation={editTarget}
        defaultType={type}
      />

      <SettleObligationModal
        open={!!settleTarget}
        onClose={() => setSettleTarget(null)}
        obligation={settleTarget}
      />

      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancelar obrigação"
        description={
          cancelTarget
            ? `A obrigação O-${cancelTarget.code} "${cancelTarget.description}" será cancelada. Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Cancelar obrigação"
        destructive
        loading={cancelMutation.isPending}
        onConfirm={handleCancel}
        onCancel={() => setCancelTarget(null)}
      />
    </div>
  )
}
